/**
 * SentinelMentionNode — `BeautifulMentionNode` subclass whose
 * `getTextContent()` returns the canonical ADR-0044 sentinel
 * `@[Display Name](user:uuid)` when the mention carries an `id`
 * data field. KAP-303.
 *
 * Why a subclass instead of a TextMatchTransformer.export:
 *
 * Live smoke against dev caught that `@lexical/markdown`'s
 * `$convertToMarkdownString` doesn't reliably invoke a text-match
 * transformer's `export` callback for `DecoratorNode` instances
 * inside paragraphs — it falls through to the decorator's
 * `getTextContent()`. The package's default `getTextContent` is
 * `${trigger}${value}` ("@Smoke User"), which silently drops the
 * user id — the data-corruption failure mode KAP-303 explicitly
 * warned about.
 *
 * Overriding `getTextContent()` at the node level forces every
 * downstream consumer (markdown export, copy-paste, drag-drop,
 * `getEditor().getTextContent()`) to see the sentinel form. The
 * read-side parser in `MarkdownRenderer` already understands the
 * sentinel via the `a[href^="user:"]` chip path, so the round-trip
 * closes.
 *
 * Per `lexical-beautiful-mentions` README:
 *   "Replace the default BeautifulMentionNode with a custom one to
 *    alter its behavior. Ensure to register your custom node in the
 *    editor config."
 */

import {
  BeautifulMentionNode,
  $isBeautifulMentionNode
} from 'lexical-beautiful-mentions';
import { serializeMention } from '../../../../lib/messageMarkdown';

export class SentinelMentionNode extends BeautifulMentionNode {
  static getType() {
    return 'sentinel-mention';
  }

  static clone(node) {
    return new SentinelMentionNode(
      node.getTrigger(),
      node.getValue(),
      node.getData(),
      node.__key
    );
  }

  static importJSON(serializedNode) {
    return new SentinelMentionNode(
      serializedNode.trigger,
      serializedNode.value,
      serializedNode.data
    );
  }

  exportJSON() {
    return {
      ...super.exportJSON(),
      type: 'sentinel-mention'
    };
  }

  getTextContent() {
    const data = this.getData() || {};
    const userId = typeof data.id === 'string' ? data.id : null;
    if (userId) {
      return serializeMention({ displayName: this.getValue(), userId });
    }
    // Defensive fallback: no id (e.g. user dragged raw text in) → emit
    // bare `@<value>`. The legacy MarkdownRenderer regex still styles
    // this per ADR-0044 §5 two-format coexistence.
    return super.getTextContent();
  }
}

/**
 * `nodes` config entry that registers SentinelMentionNode AND tells
 * Lexical to replace every BeautifulMentionNode instance the plugin
 * creates with a SentinelMentionNode. Spread into
 * `LEXICAL_INITIAL_CONFIG.nodes`.
 */
export function createSentinelMentionNodeEntries() {
  return [
    SentinelMentionNode,
    {
      replace: BeautifulMentionNode,
      with: (node) => new SentinelMentionNode(
        node.getTrigger(),
        node.getValue(),
        node.getData()
      ),
      withKlass: SentinelMentionNode
    }
  ];
}

export { $isBeautifulMentionNode };
